/**
 * VaultMoveModal: Folder picker modal for moving/copying files
 */
import { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, X } from 'lucide-react';
import type { VaultFolder } from '../../types/vault.types';
import { logError } from "../../lib/logError";

interface VaultMoveModalProps {
  isOpen: boolean;
  mode: 'move' | 'copy';
  allFolders: VaultFolder[];
  currentFolderId: string | null;
  onConfirm: (targetFolderId: string | null) => Promise<void>;
  onClose: () => void;
}

export function VaultMoveModal({
  isOpen,
  mode,
  allFolders,
  currentFolderId,
  onConfirm,
  onClose,
}: VaultMoveModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getChildren = (parentId: string | null) =>
    allFolders.filter(f => f.parent_folder_id === parentId);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(selectedId);
      onClose();
    } catch (err) {
      logError(`[Vault] ${mode} failed:`, err);
    } finally {
      setLoading(false);
    }
  };

  const renderFolder = (folder: VaultFolder, depth: number = 0): React.ReactNode => {
    const children = getChildren(folder.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(folder.id);
    const isSelected = selectedId === folder.id;
    const isCurrent = currentFolderId === folder.id;

    return (
      <div key={folder.id}>
        <div
          className={`vault-move-folder-item ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
          style={{ paddingLeft: `${16 + depth * 20}px` }}
          onClick={() => !isCurrent && setSelectedId(folder.id)}
        >
          {hasChildren ? (
            <button
              className="vault-folder-expand"
              onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id); }}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span style={{ width: 14 }} />
          )}
          {isSelected ? <FolderOpen size={16} /> : <Folder size={16} />}
          <span>{folder.name}</span>
          {isCurrent && <span className="vault-move-current-badge">current</span>}
        </div>
        {isExpanded && children.map(child => renderFolder(child, depth + 1))}
      </div>
    );
  };

  const rootFolders = getChildren(null);
  const isRootSelected = selectedId === null;

  return (
    <div className="vault-modal-overlay" onClick={onClose}>
      <div className="vault-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vault-modal-header">
          <h3>{mode === 'move' ? 'Move to Folder' : 'Copy to Folder'}</h3>
          <button className="vault-modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="vault-modal-body">
          {/* Root option */}
          <div
            className={`vault-move-folder-item ${isRootSelected ? 'selected' : ''} ${currentFolderId === null ? 'current' : ''}`}
            style={{ paddingLeft: '16px' }}
            onClick={() => currentFolderId !== null && setSelectedId(null)}
          >
            <FolderOpen size={16} />
            <span style={{ fontWeight: 600 }}>Vault Root</span>
            {currentFolderId === null && <span className="vault-move-current-badge">current</span>}
          </div>

          {rootFolders.map(folder => renderFolder(folder, 0))}

          {allFolders.length === 0 && (
            <div className="vault-modal-empty">
              <p>No folders yet. Create a folder first.</p>
            </div>
          )}
        </div>

        <div className="vault-modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={loading || (selectedId === currentFolderId)}
          >
            {loading ? 'Processing...' : mode === 'move' ? 'Move Here' : 'Copy Here'}
          </button>
        </div>
      </div>
    </div>
  );
}
